import React, { useState, useEffect } from 'react';
import { useQuery, useApolloClient } from '@apollo/react-hooks';
import { useParams } from 'react-router-dom';
import { GroupGetStartedFeedQuery } from 'services/apollo/queries';
import { LoaderDashboard, FeedListItem, FeedListInfinitySpinner, Toast } from 'components';
import { useFeatureFlagState } from 'context';
import { criticalFeedError, handleFetchMoreError } from 'services/apollo';
import { useInView } from 'react-intersection-observer';
import { GetStartedHeader, GetStartedFooter } from './..';

import styles from './GroupGetStartedFeed.module.scss';
import {
  trackGroupGetStartedSkip,
  trackGroupGetStartedComplete,
  trackGroupGetStartedAddSelectDocument,
  trackGroupGetStartedSelectDocument,
} from 'services';
import { useAddMultipleNodesToGroup } from 'services/apollo/hooks';
import { toast } from 'react-toastify';
import { useTranslation } from 'hooks';
import { useGroupGetStartedDispatch } from 'context/GroupGetStartedContext';

export const GroupGetStartedFeed = () => {
  const { groupId } = useParams();
  const { groupsFeatureFlag } = useFeatureFlagState();
  const groupGetStartedDispatch = useGroupGetStartedDispatch();
  const { t } = useTranslation();
  const client = useApolloClient();
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [selectedNodes, setSelectedNodes] = useState<string[]>([]);
  const [currentStep, setCurrentStep] = useState(1);

  const [addMultipleNodesToGroup] = useAddMultipleNodesToGroup(selectedNodes, groupId || '');

  const currentPositionInArray = currentStep - 1;

  const { error, data, fetchMore } = useQuery(GroupGetStartedFeedQuery, {
    variables: { groupId, includeGroups: groupsFeatureFlag, first: 10, cursor: '' },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const [ref, inView] = useInView({
    rootMargin: '200px 0px',
    threshold: 0,
  });

  const triggerFetchMore = async () => {
    setIsLoadingMore(true);
    const { endCursor } = data.groupById.getStartedNodes[
      currentPositionInArray
    ].commonNodes.pageInfo;

    try {
      await fetchMore({
        variables: { first: 20, cursor: endCursor },
        updateQuery: (prevResult: any, { fetchMoreResult }) => {
          fetchMoreResult.groupById.getStartedNodes[currentPositionInArray].commonNodes.nodes = [
            ...prevResult.groupById.getStartedNodes[currentPositionInArray].commonNodes.nodes,
            ...fetchMoreResult.groupById.getStartedNodes[currentPositionInArray].commonNodes?.nodes,
          ];
          setIsLoadingMore(false);
          return fetchMoreResult;
        },
      });
    } catch (e) {
      handleFetchMoreError(e);
    }
  };

  const checkedChange = (nodeId: string, nodeType: string, isChecked: boolean) => {
    let updatedSelectedNodes: string[] = [...selectedNodes];

    if (isChecked) {
      updatedSelectedNodes.push(nodeId);
      trackGroupGetStartedSelectDocument({ groupId: groupId || '', documentType: nodeType });
    } else {
      const index = updatedSelectedNodes.indexOf(nodeId);
      if (index !== -1) updatedSelectedNodes.splice(index, 1);
    }

    setSelectedNodes(updatedSelectedNodes);
  };

  const skipStep = () => {
    const newStep = currentStep + 1;

    if (newStep <= data.groupById?.getStartedNodes.length) {
      setCurrentStep(newStep);
      trackGroupGetStartedSkip({ groupId: groupId || '' });
    } else {
      trackGroupGetStartedComplete({
        groupId: groupId || '',
        addedDocumentCount: selectedNodes.length,
      });

      groupGetStartedDispatch({ type: 'COMPLETE', payload: groupId });
    }

    setSelectedNodes([]);
  };

  const addToGroup = () => {
    const newStep = currentStep + 1;

    addMultipleNodesToGroup().then((response: any) => {
      if (newStep > data.groupById?.getStartedNodes.length) {
        client.reFetchObservableQueries();
      }

      if (response?.data?.shareNodesMany?.successRate < 20) {
        toast(<Toast message={t('toasts.add-multi-nodes-to-group-error')} hasCloseButton />, {
          position: toast.POSITION.BOTTOM_LEFT,
          hideProgressBar: true,
          closeButton: false,
          closeOnClick: true,
          autoClose: 5000,
        });
      }
    });

    if (newStep <= data.groupById?.getStartedNodes.length) {
      setCurrentStep(newStep);
      trackGroupGetStartedAddSelectDocument({
        groupId: groupId || '',
        addedDocumentCount: selectedNodes.length,
      });
    } else {
      trackGroupGetStartedComplete({
        groupId: groupId || '',
        addedDocumentCount: selectedNodes.length,
      });

      groupGetStartedDispatch({ type: 'COMPLETE', payload: groupId });
    }

    setSelectedNodes([]);
  };

  useEffect(() => {
    if (inView === true) {
      triggerFetchMore().then((response) => response);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  if (data === undefined) return <LoaderDashboard layout="GroupActivityFeed" />;

  if (error && criticalFeedError(error)) {
    return <LoaderDashboard layout="GroupActivityFeed" />;
  }

  return (
    <>
      <GetStartedHeader
        name={
          data.me?.userProfile?.id ===
          data.groupById?.getStartedNodes[currentPositionInArray]?.memberId
            ? t('group-get-started.header.you')
            : data.groupById?.getStartedNodes[currentPositionInArray]?.memberName +
              t('group-get-started.header.someone')
        }
        group={data.groupById?.name}
        currentStep={currentStep}
        totalSteps={data.groupById?.getStartedNodes?.length}
      />
      <div className={styles.documentsContainer}>
        <div className={styles.documentsContainer_bin}>
          {data.groupById?.getStartedNodes[currentPositionInArray]?.commonNodes?.nodes.map(
            (node: any, key: number) => (
              <FeedListItem
                nodeId={node.id}
                nodeType={node.__typename}
                key={key}
                name={node.title}
                baseUrl={node.baseUrl}
                url={node.url}
                isIntegrationConnected={node.isIntegrationConnected}
                integrationType={node.integrationType}
                owner={node.owners[0]}
                collaborators={node.accessorInfo}
                modified={node.modified}
                created={node.created}
                nodeIcon={node.category}
                isSelectable
                checkedChange={checkedChange}
                selectedNodes={selectedNodes}
                collectionType="Grouped"
              />
            )
          )}
          {data?.groupById?.getStartedNodes[currentPositionInArray]?.commonNodes?.pageInfo
            ?.hasNextPage && (
            <div ref={ref}>
              <FeedListInfinitySpinner isLoadingMore={isLoadingMore} />
            </div>
          )}
        </div>
        <GetStartedFooter
          skip={skipStep}
          addToGroup={addToGroup}
          addToGroupDisabled={selectedNodes.length === 0}
        />
      </div>
    </>
  );
};
